/* eslint-disable @typescript-eslint/camelcase */
import { Component, Vue } from 'vue-property-decorator'
import { namespace } from 'vuex-class'
import { isUndefined, isNull } from 'lodash/fp'
import LoadingBar from '@core/components/LoadingBar.vue'
import { inject } from 'inversify-props'
import API from '@core/services/API'
import { IProjectInfo } from '@master/config/IProjectInfo'
import AppDialog from '@core/components/AppDialog.vue'
import RequestResetPassword from '@core/pages/RequestResetPassword.vue'
import { UIService } from '@core/services/UIService'
import { SharedVariable } from '@core/model/SharedVariable'
import { IProjectSettings } from '@master/config/IProjectSettings'
import { UserMeta } from '@core/model/common/UserMeta'

const userStore = namespace('UserStore')
@Component({
  components: {
    LoadingBar,
    AppDialog,
    RequestResetPassword
  }
})
export class AdminLoginMixin extends Vue {
  private loginNotice = ''
  private username = ''
  private password = ''
  private disabled = false
  private otp: boolean = null
  private isSingpass = false
  private singpassUrl = '#'

  private showOtp = false
  private otpPrefix = ''
  private otpState = ''
  private otpToken = ''
  private email = ''
  private timer = 60
  private isResendDisabled = true
  private otpNotice = ''

  @userStore.State
  private userList: UserMeta[]

  @userStore.Action
  private fetchUserList: () => Promise<void>

  @inject()
  private $api: API

  @inject()
  private projectInfo: IProjectInfo

  @inject()
  private projectSettings: IProjectSettings

  uiService: UIService

  @inject()
  private sharedVariable: SharedVariable

  async mounted () {
    // take injected UIService and set it to local
    this.uiService = this.sharedVariable.uiService

    await this.getSystemInformation()
    if (this.isSingpass) await this.getSingpassSetting()
  }

  get displayBrandName () {
    return this.projectSettings.displayBrandInLogin
  }

  async getSystemInformation () {
    const { singpass_login: singpassLogin, login_otp } = await this.$api.system.information()
    this.isSingpass = singpassLogin
    this.otp = login_otp
  }

  async getSingpassSetting () {
    const { clientId, redirectUri, state, nonce } = await this.$api.account.singpassSettings()
    this.singpassUrl = `${__SINGPASS_URL__}/auth?client_id=${clientId}&redirect_uri=${redirectUri}&scope=openid&response_type=code&state=${state}&nonce=${nonce}`
  }

  async onSubmit () {
    this.disabled = true

    if (this.otp) {
      const login = await this.$api.account.otpRequest({
        accountName: this.username,
        accountPass: this.password
      })
        .then(async response => {
          if (response && response.prefix && response.state) {
            this.loginNotice = 'Login successful'
            this.email = response.email
            return response
          }

          if (response.error) {
            if (response.error.length > 100) {
              console.error('login.js: response error', response.error)
              this.loginNotice = 'Login Exception'
              await this.uiService.alert.show({
                type: 'error',
                message: response.error
              })
            } else {
              console.info('login.js: Failed', response.error)
              this.loginNotice = 'Login Failed, ' + response.error
            }
          }
        })
        .catch(async error => {
          console.error('login.js: Exception', error)
          await this.uiService.alert.show({
            type: 'error',
            message: error
          })
        })
        .finally(() => {
          this.disabled = false
        })

      if (!isNull(login) && !isUndefined(login)) {
        this.otpPrefix = login.prefix
        this.otpState = login.state
        this.disabled = false
        this.showOtp = true

        this.isResendDisabled = true
        this.timer = 60
        this.countDownTimer()
      }
    } else {
      const options = {
        headers:
        {
          'content-type': 'application/x-www-form-urlencoded; charset=UTF-8'
        }
      }
      const login = await this.$api.account.login({
        accountName: this.username,
        accountPass: this.password
      }, options)
        .then(async response => {
          if (response && response.isLogin) {
            this.loginNotice = 'Login successful, welcome ' + response.accountNames[0]
            return response
          }

          if (response.error) {
            if (response.error.length > 100) {
              console.error('login.js: response error', response.error)
              this.loginNotice = 'Login Exception'
              await this.uiService.alert.show({
                type: 'error',
                message: response.error
              })
            } else {
              console.info('login.js: Failed', response.error)
              this.loginNotice = response.error
            }
          } else {
            console.info('login.js: Failed', response)
            this.loginNotice = 'Login Failed (wrong password?)'
          }
        })
        .catch(async error => {
          console.error('login.js: Exception', error)
          await this.uiService.alert.show({
            type: 'error',
            message: error
          })
        })
        .finally(() => {
          this.disabled = false
        })

      if (!isNull(login) && !isUndefined(login)) {
        const info = await this.getAccountInfo(login.accountID)
        const meta = await this.getAccountMeta(login.accountID)
        await this.$store.dispatch('login', { ...login, ...info, ...meta })

        // After the account info has been retrieved,
        // redirect the user to the page
        this.$emit('redirect', meta.systemrole)
      }
    }
  }

  countDownTimer () {
    if (this.timer > 0) {
      setTimeout(() => {
        this.timer -= 1
        this.countDownTimer()
      }, 1000)
    } else {
      this.isResendDisabled = false
    }
  }

  async onResend () {
    await this.$api.account.otpRefresh({
      state: this.otpState
    })
      .then(response => {
        if (response.error) {
          console.error('login.js: response error', response.error)
          this.otpNotice = 'Request OTP Failed, ' + response.error
          return
        }

        this.otpNotice = 'A new OTP has been sent to your email'
        this.otpPrefix = response.prefix
        this.otpState = response.state

        // start timer again
        this.isResendDisabled = true
        this.timer = 60
        this.countDownTimer()
      })
      .catch(async error => {
        console.error('login.js: Exception', error)
        await this.uiService.alert.show({
          type: 'error',
          message: error
        })
      })
  }

  async onSubmitOtp () {
    await this.$api.account.otpLogin({
      state: this.otpState,
      token: this.otpToken
    })
      .then(response => {
        if (response.error || !response.isLogin) {
          console.error('login.js: response error', response.error)
          this.otpNotice = 'Submit OTP Failed, ' + response.error
          return
        }

        this.otpNotice = 'OTP verified, login success'
        this.checkLogin()
      })
      .catch(async error => {
        console.error('login.js: Exception', error)
        await this.uiService.alert.show({
          type: 'error',
          message: error
        })
      })
  }

  async checkLogin () {
    const login = await this.getAccountLogin()

    if (!isNull(login) && !isUndefined(login)) {
      console.log(`Welcome ${login.accountNames[0]}!`)
      const info = await this.getAccountInfo(login.accountID)
      const meta = await this.getAccountMeta(login.accountID)
      await this.$store.dispatch('login', { ...login, ...info, ...meta })

      if (this.userList?.length === 0) {
        await this.fetchUserList()
      }

      // After the account info has been retrieved,
      // redirect the user to the page
      this.$emit('redirect', meta.systemrole)
    }
  }

  getAccountLogin () {
    return this.$api.account.loginGet()
      .then(response => {
        if (response && response.isLogin) {
          return response
        }
      })
      .catch(async error => {
        console.error('login.js: Exception', error)
        await this.uiService.alert.show({
          type: 'error',
          message: error
        })
      })
  }

  async getAccountInfo (accountId: string) {
    return await this.$api.account.loginInfoGet({
      accountID: accountId
    })
  }

  async getAccountMeta (accountId: string) {
    const response = await this.$api.account.loginMetaGet({
      _oid: accountId
    })
    return response.meta
  }

  onFocusInput (e) {
    e.target.previousSibling.classList.add('active')
  }

  onBlurInput (e) {
    if (!e.target.value) e.target.previousSibling.classList.remove('active')
  }
}
